import { useMemo } from 'react';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import type { MessageTestingCell } from '@trinity/chat/types/message-testing';
import { ScoreBadge, COLORS } from './ScoreBadge';
import type { SegmentOption, TestableMessage } from './types';

interface MessageTestingFlatTableProps {
  messages: TestableMessage[];
  segments: SegmentOption[];
  cells: MessageTestingCell[];
  cellErrors: Map<string, string>;
}

export function MessageTestingFlatTable({
  messages,
  segments,
  cells,
  cellErrors,
}: MessageTestingFlatTableProps): React.JSX.Element {
  const cellMap = useMemo(() => {
    const map = new Map<string, Map<string, MessageTestingCell>>();
    for (const c of cells) {
      if (!map.has(c.messageId)) map.set(c.messageId, new Map());
      map.get(c.messageId).set(c.segmentId, c);
    }
    return map;
  }, [cells]);

  return (
    <ListScroller>
      {messages.map((msg) => (
        <MessageSection key={msg.id}>
          <MessageHeader>
            <Tooltip title={msg.body} placement="top" enterDelay={300}>
              <MessageTitle>
                {msg.code?.trim() ? <MessageCode>{msg.code}</MessageCode> : null}
                {msg.displayTitle}
              </MessageTitle>
            </Tooltip>
          </MessageHeader>
          <SegmentList>
            {segments.map((seg) => {
              const cell = cellMap.get(msg.id)?.get(seg.identifier);
              const cellError = cellErrors.get(`${msg.id}:${seg.identifier}`);
              let cellContent: React.ReactNode;
              if (cell) {
                cellContent = <ScoreBadge cell={cell} />;
              } else if (cellError) {
                cellContent = <ErrorBadge>Error</ErrorBadge>;
              } else {
                cellContent = <PendingBadge>&mdash;</PendingBadge>;
              }
              return (
                <SegmentRow key={seg.identifier}>
                  <SegmentNameCell>{seg.name}</SegmentNameCell>
                  <ScoreCell>
                    {cellContent}
                  </ScoreCell>
                </SegmentRow>
              );
            })}
          </SegmentList>
        </MessageSection>
      ))}
    </ListScroller>
  );
}

// ─── Styled ─────────────────────────────────────────────────────────────────

const ListScroller = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MessageSection = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
`;

const MessageHeader = styled.div`
  padding: 8px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const MessageTitle = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #1a1a1a;
  cursor: default;
  display: block;
  word-break: break-word;
  white-space: normal;
  line-height: 1.4;
`;

const MessageCode = styled.strong`
  font-size: 12px;
  font-weight: 700;
  color: #1271c4;
  margin-right: 6px;

  &::after {
    content: ':';
  }
`;

const SegmentList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SegmentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }

  &:hover {
    background: #fafafa;
  }
`;

const SegmentNameCell = styled.div`
  padding: 6px 12px;
  font-size: 12px;
  color: #1a1a1a;
  flex: 1;
  min-width: 0;
`;

const ScoreCell = styled.div`
  padding: 4px 12px;
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
`;

const PendingBadge = styled.span`
  color: #bbb;
  font-size: 14px;
`;

const ErrorBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  background: ${COLORS.red.bg};
  border: 1px solid ${COLORS.red.border};
  color: ${COLORS.red.text};
  font-size: 11px;
  font-weight: 600;
`;
